<template>
  <div class="fish-meter" :class="[`fish-meter--${tamanho}`, { 'fish-meter--cheio': quaseCheio }]">
    <!--
      No medidor FINO o número sai de dentro do trilho e vem para cá.

      O trilho fino tem 16px de altura e 6px de borda de cada lado: com
      `box-sizing: border-box`, sobram **4px** de miolo, e o número de 14px
      dentro dele era cortado pelo `overflow: hidden` — uma tarja borrada em vez
      de "54/70" (visto nas fichas de integrante, 09/09/2026). Ao lado do rótulo
      ele cabe, e a leitura fica a de sempre: nome à esquerda, número à direita.
    -->
    <div v-if="rotulo || $slots.rotulo || numeroDeFora" class="fish-meter__rotulo t-rotulo">
      <slot name="rotulo">{{ rotulo }}</slot>
      <span v-if="numeroDeFora" class="fish-meter__numero-fora t-numero">{{ textoDoNumero }}</span>
    </div>

    <div
      class="fish-meter__trilho"
      role="progressbar"
      :aria-valuenow="Math.round(valor)"
      :aria-valuemin="0"
      :aria-valuemax="Math.round(maximo)"
      :aria-label="rotulo || rotuloAcessivel"
      :aria-valuetext="textoAcessivel"
    >
      <div class="fish-meter__tampa" :class="`fish-meter__tampa--${cor}`" :style="{ width: `${porcentagem}%` }" />
      <span v-if="mostrarNumero && !numeroDeFora" class="fish-meter__numero t-numero">
        {{ textoDoNumero }}
      </span>
    </div>
  </div>
</template>

<script>
/**
 * 2.4 — `FishMeter`: energia, XP, progresso de nível, enchimento do armazém (D26), meta do clã (D22).
 *
 * ## Por que ele é desenhado e não vem do craftpix (0.7)
 *
 * O documento mandava testar as barras do craftpix (`Healthbar_*`, `Energybar_*`, 96×12) antes de
 * descartar o pack inteiro. **Testadas e descartadas:** são plásticas e brilhantes, com realce
 * especular duro no topo, e a de vida vem **segmentada em três blocos** — vocabulário de unitframe
 * de MMO de combate, a mesma língua industrial pela qual o D2 recusou o pack. A exceção não se
 * confirmou.
 *
 * O medidor daqui é aro de madeira e miolo afundado, **sem brilho**.
 *
 * ## O aviso de "quase cheio" é SUAVE (D0, D26)
 *
 * O armazém dos Negócios enche e a pesca dos funcionários para — e hoje *"o jogador descobre quando
 * o número parar de subir"*. O medidor avisa antes. Mas com brilho, nunca com alarme: o D0 proíbe
 * badge vermelho e urgência fabricada, e um medidor pulsando em vermelho é as duas coisas.
 *
 * ## `role="progressbar"` (UI-30)
 *
 * O cliente tinha **dois** arquivos com atributos `aria-*` no total. Barra de progresso sem
 * `role="progressbar"` é, para um leitor de tela, uma `div` vazia — o jogador cego não sabe que
 * existe energia, quanto menos quanta.
 *
 * O `aria-valuetext` existe porque `aria-valuenow="8"` é lido como "oito" e não diz de quê. Com
 * ele, o leitor anuncia "8 de 20 — Energia", que é a informação de verdade.
 */
export default {
  name: 'FishMeter',
  props: {
    valor: { type: Number, required: true },
    maximo: { type: Number, default: 100 },
    rotulo: { type: String, default: '' },
    rotuloAcessivel: { type: String, default: 'Progresso' },
    cor: {
      type: String,
      default: 'ouro',
      validator: (v) => ['ouro', 'agua', 'bom', 'ruim', 'parallelium'].includes(v),
    },
    tamanho: {
      type: String,
      default: 'medio',
      validator: (v) => ['fino', 'medio', 'grosso'].includes(v),
    },
    mostrarNumero: { type: Boolean, default: false },
    /** O sufixo do número: "kg" para o armazém, "" para energia. */
    unidade: { type: String, default: '' },
    /** A partir de quanto o medidor avisa que está quase cheio. `0` desliga o aviso. */
    limiarDeAviso: { type: Number, default: 0 },
  },
  computed: {
    /** O trilho fino não comporta texto: 16px de altura menos 12px de borda. */
    numeroDeFora() {
      return this.mostrarNumero && this.tamanho === 'fino';
    },
    porcentagem() {
      if (!this.maximo || this.maximo <= 0) return 0;
      // Preso entre 0 e 100: um valor acima do máximo (dívida, bônus temporário) estouraria a caixa
      // e empurraria o layout — e altura/largura fixa estourada é o UI-17 de novo.
      return Math.max(0, Math.min(100, (this.valor / this.maximo) * 100));
    },
    quaseCheio() {
      return this.limiarDeAviso > 0 && this.porcentagem >= this.limiarDeAviso;
    },
    textoDoNumero() {
      return `${this.formatar(this.valor)}/${this.formatar(this.maximo)}${this.unidade}`;
    },
    textoAcessivel() {
      return `${this.textoDoNumero}${this.rotulo ? ` — ${this.rotulo}` : ''}`;
    },
  },
  methods: {
    formatar(n) {
      return Number.isInteger(n) ? n.toLocaleString('pt-BR') : n.toFixed(1);
    },
  },
};
</script>

<style scoped>
.fish-meter {
  width: 100%;
}

.fish-meter__rotulo {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-2);
  margin-bottom: var(--e-1);
  /* `.t-rotulo` pinta em `--texto-fraco`, que é a família de fundo ESCURO: sobre
     papel ela quase some — foi o "HP" fantasma das fichas. Herdando, o medidor
     fica com a cor de quem o contém, que é tinta no papel e texto no mar. */
  color: inherit;
}

/* `margin-left: auto` e não só o `space-between` do pai: sem rótulo o número é
   filho único, e filho único no `space-between` encosta à ESQUERDA. */
.fish-meter__numero-fora {
  flex: 0 0 auto;
  margin-left: auto;
  opacity: 0.85;
}

.fish-meter__trilho {
  position: relative;
  width: 100%;
  border: 6px solid transparent;
  border-image: url('/ui/medidor-fundo.png') 6 / 6px repeat;
  background: var(--fundo-encaixe);
  background-clip: padding-box;
  image-rendering: pixelated;
  overflow: hidden;
}

.fish-meter--fino .fish-meter__trilho {
  height: 16px;
}
.fish-meter--medio .fish-meter__trilho {
  height: 24px;
}
.fish-meter--grosso .fish-meter__trilho {
  height: 32px;
}

.fish-meter__tampa {
  height: 100%;
  /* Só `width`. Nunca `transition: all` (UI-24) — aqui ele animaria também a cor de fundo e a
   * borda, e um medidor que muda de cor gradualmente ao encher parece defeito. */
  transition: width var(--t-lento) var(--curva-entrada);
  image-rendering: pixelated;
}

.fish-meter__tampa--ouro {
  background: linear-gradient(to bottom, var(--ouro-claro) 0 25%, var(--ouro) 25% 75%, var(--latao) 75%);
}
.fish-meter__tampa--agua {
  background: linear-gradient(to bottom, var(--agua-espuma) 0 25%, var(--agua-turquesa) 25% 75%, var(--agua-rasa) 75%);
}
.fish-meter__tampa--bom {
  background: linear-gradient(to bottom, #9cc189 0 25%, var(--cor-bom) 25% 75%, #5d7f4c 75%);
}
.fish-meter__tampa--ruim {
  background: linear-gradient(to bottom, #c98876 0 25%, var(--cor-ruim) 25% 75%, #7d4235 75%);
}
.fish-meter__tampa--parallelium {
  background: linear-gradient(
    to bottom,
    var(--parallelium-claro) 0 25%,
    var(--parallelium) 25% 75%,
    #55487a 75%
  );
}

/* O degradê em três faixas DURAS (`0 25%`, `25% 75%`, `75%`) e não contínuo: um degradê suave é
 * aerografia, e pixel art sombreia em bandas. É o mesmo bisel do botão, na horizontal. */

.fish-meter__numero {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--f-2);
  color: var(--texto);
  /* O contorno de texto: o número fica legível tanto sobre a parte cheia quanto sobre a vazia, sem
   * precisar de uma caixa atrás dele. */
  text-shadow: 1px 1px 0 var(--agua-abissal), -1px 1px 0 var(--agua-abissal),
    1px -1px 0 var(--agua-abissal), -1px -1px 0 var(--agua-abissal);
}

/* O aviso de quase cheio: brilho suave, e ele RESPIRA (4s), não pisca (D0, D26). */
.fish-meter--cheio .fish-meter__trilho {
  animation: repouso-brilhar 4s var(--curva-agua) infinite;
}

@media (prefers-reduced-motion: reduce) {
  .fish-meter--cheio .fish-meter__trilho {
    animation: none;
    box-shadow: var(--brilho-suave);
  }
}
</style>
